import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const STORAGE_PREFIX = "ecotour:scroll:v1:";
const MAX_RESTORE_FRAMES = 120;

const routeKeyFor = (location) => `${location.pathname || "/"}${location.search || ""}`;

const readPosition = (key) => {
  if (typeof window === "undefined" || !window.sessionStorage) return 0;
  try {
    return Math.max(0, Number(window.sessionStorage.getItem(`${STORAGE_PREFIX}${key}`)) || 0);
  } catch {
    return 0;
  }
};

const writePosition = (key, value) => {
  if (typeof window === "undefined" || !window.sessionStorage || !key) return;
  try {
    window.sessionStorage.setItem(`${STORAGE_PREFIX}${key}`, String(Math.max(0, Math.round(value || 0))));
  } catch {
    // Browser history can still provide its native fallback.
  }
};

export function RouteScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const previousRef = useRef(null);
  const currentRouteKey = routeKeyFor(location);
  const currentEntryKey = location.key || currentRouteKey;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previousRestoration;
    };
  }, []);

  useEffect(() => {
    const previous = previousRef.current;
    if (previous) {
      writePosition(`entry:${previous.entryKey}`, previous.scrollY);
      writePosition(`route:${previous.routeKey}`, previous.scrollY);
    }

    const entryKey = currentEntryKey;
    const routeKey = currentRouteKey;
    const target = navigationType === "POP"
      ? readPosition(`entry:${entryKey}`) || readPosition(`route:${routeKey}`)
      : 0;
    let frame = 0;
    let frameId;
    let cancelled = false;

    const restore = () => {
      if (cancelled) return;
      const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      if (target <= maxScroll || frame >= MAX_RESTORE_FRAMES) {
        window.scrollTo({ top: Math.min(target, maxScroll), left: 0, behavior: "auto" });
        return;
      }
      frame += 1;
      frameId = window.requestAnimationFrame(restore);
    };
    frameId = window.requestAnimationFrame(restore);

    const current = { entryKey, routeKey, scrollY: target };
    previousRef.current = current;
    const capture = () => {
      current.scrollY = window.scrollY || window.pageYOffset || 0;
    };
    window.addEventListener("scroll", capture, { passive: true });

    return () => {
      cancelled = true;
      if (frameId) window.cancelAnimationFrame(frameId);
      capture();
      writePosition(`entry:${entryKey}`, current.scrollY);
      writePosition(`route:${routeKey}`, current.scrollY);
      window.removeEventListener("scroll", capture);
    };
  }, [currentEntryKey, currentRouteKey, navigationType]);

  return null;
}

export const __clearRouteScrollRestorationForTests = () => {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  Object.keys(window.sessionStorage)
    .filter((key) => key.startsWith(STORAGE_PREFIX))
    .forEach((key) => window.sessionStorage.removeItem(key));
};

export default RouteScrollRestoration;
